import { ContainerClient } from '@azure/storage-blob'
import { Alert, FormHelperText } from '@mui/material'
import type React from 'react'
import { useContext, useRef, useState } from 'react'
import styled from 'styled-components'

import Button from '@/components/Button'
import type { SelectOption } from '@/components/Select'
import Select from '@/components/Select'
import ctx from '@/context/AppContext'
import type { FetchSingleApplication } from '@/hooks/useSubject'
import type { Application } from '@/types/Activity'
import { genericErrorMessage } from '@/utils/error'

import SlideInSidebar from './DEPRECATED_UI/SlideInSidebar'

const Container = styled.div`
  margin: 10px;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 1px 9px 0 rgba(128, 128, 128, 0.26);
  background: white;
  min-width: 320px;
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const DropZone = styled.div<{ dragging: boolean; hasFiles: boolean }>`
  position: relative;
  background-color: #fff;
  border: ${({ dragging }) => (dragging ? '2px' : '1px')} dashed
    ${({ dragging, hasFiles }) =>
      dragging ? '#34bebd' : hasFiles ? '#34bebd' : '#bcbec0'};
  border-radius: 10px;
  padding: 16px;
  cursor: pointer;
  text-align: center;
  color: #55616b;

  &:focus-visible {
    outline: 2px solid #34bebd;
    outline-offset: 2px;
  }

  input {
    opacity: 0;
    width: 1px;
    height: 1px;
    position: absolute;
  }

  p {
    margin: 8px 0 0;
    font-size: 0.875rem;
  }
`

const DropIcons = styled.div`
  img {
    height: 40px;
  }
`

const FileList = styled.ul`
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  text-align: left;
`

const FileItem = styled.li<{ hasError: boolean }>`
  font-weight: 600;
  color: ${({ hasError }) => (hasError ? '#d32f2f' : '#34bebd')};
  font-size: 0.875rem;
  padding: 2px 0;
`

const Controls = styled.div`
  display: flex;
  gap: 8px;
`

const ALLOWED_MIME = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
])

const DOCUMENT_TYPE_OPTIONS: SelectOption<string>[] = [
  // Identity
  { label: 'Passport', value: 'passport' },
  { label: "Driver's licence", value: 'driving licence' },
  { label: 'Identity card', value: 'identity card' },
  { label: 'UK immigration status document', value: 'uk immigration status document' },
  { label: 'Visa', value: 'visa' },
  { label: 'Other identity document', value: 'other identity document' },
  // Income
  { label: 'Payslip', value: 'payslip' },
  { label: 'Bank statement', value: 'bank statement' },
  { label: 'Tax return', value: 'taxreturn' },
  { label: 'Signed contract of employment', value: 'signedcontractofemployment' },
  { label: 'Financial statement', value: 'financial statement' },
  { label: 'Tax letter', value: 'tax letter' },
  { label: 'Mortgage', value: 'mortgage' },
  { label: 'Pension letter', value: 'pension letter' },
  { label: 'Employment document', value: 'employmentdocument' },
  { label: 'Benefit document', value: 'benefitdocument' },
  // Residential
  { label: 'Proof of address', value: 'proof of address' },
  { label: 'Household bill', value: 'household bill' },
  { label: 'Utilities bill', value: 'utilities bill' },
  { label: 'Lease / rent document', value: 'lease / rent' },
  // Other
  { label: 'Additional document', value: 'additionaldocument' },
]

interface PendingFile {
  blob: File
  originalName: string
  error?: string
}

interface Props {
  application: Application
  onClose: Function
  fetchSingleApplication: FetchSingleApplication
}

const UploadPanel: React.FC<Props> = ({
  application,
  fetchSingleApplication,
  onClose,
}) => {
  const { personId, accessKey } = useContext(ctx)
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const [dragging, setDragging] = useState(false)
  const [fileType, setFileType] = useState('')
  const [showValidation, setShowValidation] = useState(false)
  const [uploadError, setUploadError] = useState<string | undefined>(undefined)
  const [isUploading, setIsUploading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  if (!application) return <div>Loading...</div>

  const fetchUploadToken = async () => {
    const tokenResponse = await fetch(
      `${import.meta.env.VITE_VOUCH_API}/media/container-sas`,
      {
        method: 'GET',
        headers: {
          'Ocp-Apim-Subscription-Key': accessKey,
        },
      },
    )

    if (!tokenResponse.ok) {
      throw new Error(`Failed to retrieve upload token (${tokenResponse.status}).`)
    }

    const { sasUrl } = await tokenResponse.json()

    return { blobToken: sasUrl }
  }

  const uploadToBlob = async (file: File, blobToken: string): Promise<void> => {
    const containerClient = new ContainerClient(blobToken)
    const blockBlobClient = containerClient.getBlockBlobClient(file.name)
    await blockBlobClient.uploadData(file)
  }

  const handleFilesChosen = (rawFiles: FileList | File[]) => {
    const files = Array.from(rawFiles)
    const rejected = files.filter(f => !ALLOWED_MIME.has(f.type))
    if (rejected.length > 0) {
      setPendingFiles([])
      setUploadError('Only images and PDFs are accepted.')
      return
    }
    const pending: PendingFile[] = files.map(rawFile => {
      const lastDot = rawFile.name.lastIndexOf('.')
      const ext = lastDot !== -1 ? rawFile.name.substring(lastDot).toLowerCase() : ''
      const blobname = `manual-dashboard-upload_${crypto.randomUUID()}${ext}`
      return {
        blob: new File([rawFile], blobname, { type: rawFile.type }),
        originalName: rawFile.name,
      }
    })
    setPendingFiles(pending)
    setUploadError(undefined)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setDragging(false)
    if (isUploading) return
    if (e.dataTransfer.files?.length) {
      handleFilesChosen(e.dataTransfer.files)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isUploading) return
    if (e.target.files?.length) {
      handleFilesChosen(e.target.files)
    }
    // Clear value so selecting the same file twice still fires `change`.
    e.target.value = ''
  }

  const handleClose = () => {
    if (isUploading) return
    onClose()
  }

  const handleUploadClick = async () => {
    if (isUploading) return

    if (!pendingFiles.length || !fileType) {
      setShowValidation(true)
      return
    }

    if (!application.customerId || !application.subjectId) {
      setUploadError('Missing required application data — cannot upload.')
      return
    }

    // Snapshot inputs so concurrent state changes (drops, type swaps) cannot
    // mis-attribute errors or alter the API payload mid-flight.
    const snapshotFiles = pendingFiles
    const snapshotFileType = fileType

    setUploadError(undefined)
    setIsUploading(true)

    // Two-step upload: blob write then API registration. If the blob succeeds but
    // the API call fails, the file sits in the ingest container without a document
    // record until manually cleaned up. A TTL/lifecycle policy on the ingest
    // container is the recommended long-term mitigation.
    let blobToken: string
    try {
      ;({ blobToken } = await fetchUploadToken())
    } catch (err) {
      console.error('[UploadPanel] failed to fetch upload token', err)
      setUploadError(genericErrorMessage)
      setIsUploading(false)
      return
    }

    const blobResults = await Promise.allSettled(
      snapshotFiles.map(pf => uploadToBlob(pf.blob, blobToken)),
    )

    const hasFailures = blobResults.some(r => r.status === 'rejected')
    if (hasFailures) {
      setPendingFiles(
        snapshotFiles.map((pf, i) =>
          blobResults[i].status === 'rejected'
            ? { ...pf, error: genericErrorMessage }
            : pf,
        ),
      )
      blobResults.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.error(
            `[UploadPanel] blob upload failed for ${snapshotFiles[i].originalName}`,
            (r as PromiseRejectedResult).reason,
          )
        }
      })
      setIsUploading(false)
      return
    }

    const documents = Object.fromEntries(
      snapshotFiles.map((pf, i) => [`file-${i}`, pf.blob.name]),
    )

    try {
      const apiRes = await fetch(
        `${import.meta.env.VITE_VOUCH_API}/subject/${encodeURIComponent(
          application.subjectId,
        )}/documents-upload`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Ocp-Apim-Subscription-Key': accessKey,
            'vouch-customer-ref': application.customerId,
            ...(personId ? { userId: personId } : {}),
          },
          method: 'POST',
          body: JSON.stringify({
            documents,
            fileType: snapshotFileType,
          }),
        },
      )

      if (!apiRes.ok) {
        throw new Error(`[UploadPanel] API ${apiRes.status}`)
      }
    } catch (err) {
      console.error('[UploadPanel] upload error', err)
      setUploadError(genericErrorMessage)
      setIsUploading(false)
      return
    }

    setIsUploading(false)
    await fetchSingleApplication(application.customerId, application.subjectId)
  }

  return (
    <SlideInSidebar
      onClose={handleClose}
      header={<h1>Upload</h1>}
      content={
        <Container>
          <Body>
            <p>
              Here you can upload additional documents for: <br /> <br />
              <strong>
                {application?.context?.firstName} {application?.context?.lastName}
              </strong>{' '}
              - {application?.context?.email}
            </p>
            <DropZone
              dragging={dragging}
              hasFiles={pendingFiles.length > 0}
              role="button"
              tabIndex={0}
              onClick={() => fileRef.current?.click()}
              onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  fileRef.current?.click()
                }
              }}
              onDragOver={e => {
                e.preventDefault()
                setDragging(true)
              }}
              onDragLeave={e => {
                e.preventDefault()
                setDragging(false)
              }}
              onDrop={handleDrop}
            >
              <input
                ref={fileRef}
                type="file"
                multiple
                tabIndex={-1}
                accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                onChange={handleInputChange}
              />
              <DropIcons>
                <img src="/assets/svg/click.svg" alt="" />{' '}
                <img src="/assets/svg/drag.svg" alt="" />
              </DropIcons>
              {pendingFiles.length > 0 ? (
                <FileList>
                  {pendingFiles.map((pf, i) => (
                    <FileItem key={i} hasError={!!pf.error}>
                      {pf.originalName}
                      {pf.error && ` — ${pf.error}`}
                    </FileItem>
                  ))}
                </FileList>
              ) : (
                <p>Click or drag to select files</p>
              )}
            </DropZone>
            {showValidation && !pendingFiles.length && (
              <FormHelperText error>Please select at least one file</FormHelperText>
            )}
            <div>
              <Select
                name="document-type"
                label="Document type *"
                ariaLabel="Document type"
                selectedValue={fileType}
                options={[
                  { value: '', label: 'Please select' },
                  ...DOCUMENT_TYPE_OPTIONS,
                ]}
                onSelect={value => {
                  setFileType(value)
                  setShowValidation(false)
                  setUploadError(undefined)
                }}
              />
              {showValidation && !fileType && (
                <FormHelperText error>Please select a document type</FormHelperText>
              )}
            </div>
            {uploadError && <Alert severity="error">{uploadError}</Alert>}
            <Controls>
              <Button onClick={handleUploadClick} disabled={isUploading}>
                {isUploading ? 'Uploading…' : 'Upload'}
              </Button>
              <Button $alternate onClick={handleClose} disabled={isUploading}>
                Close
              </Button>
            </Controls>
          </Body>
        </Container>
      }
    />
  )
}

export default UploadPanel
